import React, { useState, useEffect } from "react";
import {
  useStripe,
  useElements,
  CardElement,
  PaymentRequestButtonElement,
} from "@stripe/react-stripe-js";
import LoadingSpinner from "../LoadingSpinner";
import ErrorMessage from "../ErrorMessage";
import { subscriptionAPI } from "../../services/api";
import { handleStripeError } from "../../services/stripeService";

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: "16px",
      color: "#424770",
      fontFamily:
        'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif',
      "::placeholder": {
        color: "#aab7c4",
      },
      padding: "12px",
    },
    invalid: {
      color: "#9e2146",
      iconColor: "#9e2146",
    },
    complete: {
      color: "#059669",
      iconColor: "#059669",
    },
  },
  hidePostalCode: false,
  iconStyle: "solid",
};

const StripeCheckout = ({
  subscription,
  onSuccess,
  onError,
  disabled = false,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState(null);

  // Initialize Payment Request (Apple Pay, Google Pay, etc.)
  useEffect(() => {
    if (!stripe) return;

    const pr = stripe.paymentRequest({
      country: "US",
      currency: "usd",
      total: {
        label: `${subscription.tier === "standard" ? "Standard" : "Premium"} Plan`,
        amount: Math.round(subscription.displayPrice * 100), // Convert to cents
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    // Check if Payment Request is available
    pr.canMakePayment().then((result) => {
      if (result) {
        setPaymentRequest(pr);
      }
    });

    pr.on("paymentmethod", async (ev) => {
      try {
        setProcessing(true);
        setError(null);

        const subscriptionData = {
          tier: subscription.tier,
          account_limit: subscription.accountLimit,
          billing_cycle: subscription.billingCycle,
          payment_method_id: ev.paymentMethod.id,
        };

        // Create subscription via API service
        const result = await subscriptionAPI.create(subscriptionData);

        if (result.client_secret) {
          // Confirm payment if required (3D Secure)
          const { error: confirmError } = await stripe.confirmCardPayment(
            result.client_secret,
          );
          if (confirmError) {
            throw new Error(confirmError.message);
          }
        }

        ev.complete("success");
        onSuccess(result);
      } catch (err) {
        console.error("Payment Request error:", err);
        ev.complete("fail");
        setError(err.message || "Payment failed");
        onError(err);
      } finally {
        setProcessing(false);
      }
    });
  }, [stripe, subscription, onSuccess, onError]);

  // Handle card element changes
  const handleCardChange = (event) => {
    setCardComplete(event.complete);
    setCardError(event.error ? event.error.message : null);
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || processing || disabled) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const cardElement = elements.getElement(CardElement);

      // Create payment method
      const { error: paymentMethodError, paymentMethod } =
        await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
          billing_details: {
            email: subscription.customerEmail,
          },
        });

      if (paymentMethodError) {
        throw new Error(paymentMethodError.message);
      }

      // Create subscription
      const subscriptionData = {
        tier: subscription.tier,
        account_limit: subscription.accountLimit,
        billing_cycle: subscription.billingCycle,
        payment_method_id: paymentMethod.id,
      };

      const result = await subscriptionAPI.create(subscriptionData);

      // Handle 3D Secure authentication if required
      if (result.client_secret) {
        const { error: confirmError } = await stripe.confirmCardPayment(
          result.client_secret,
        );
        if (confirmError) {
          throw new Error(confirmError.message);
        }
      }

      onSuccess(result);
    } catch (err) {
      console.error("Subscription error:", err);
      const errorMessage =
        handleStripeError(err) || err.message || "An unexpected error occurred";
      setError(errorMessage);
      onError(err);
    } finally {
      setProcessing(false);
    }
  };

  if (!stripe) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="md" />
        <span className="ml-2 text-gray-600">Loading payment form...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Display */}
      {error && <ErrorMessage message={error} />}

      {/* Payment Request Button (Apple Pay, Google Pay, etc.) */}
      {paymentRequest && (
        <div>
          <div className="text-center mb-4">
            <span className="text-sm text-gray-500">Quick payment options</span>
          </div>
          <PaymentRequestButtonElement
            options={{
              paymentRequest,
              style: {
                paymentRequestButton: {
                  theme: "dark",
                  height: "48px",
                  type: "default",
                },
              },
            }}
          />
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">
                Or pay with card
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Card Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Payment Information
          </label>

          <div
            className={`border rounded-lg p-4 bg-white transition-colors ${
              cardError
                ? "border-red-500 bg-red-50"
                : cardComplete
                  ? "border-green-500 bg-green-50"
                  : "border-gray-300 focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500"
            }`}
          >
            <CardElement
              options={CARD_ELEMENT_OPTIONS}
              onChange={handleCardChange}
            />
          </div>

          {/* Card validation error */}
          {cardError && (
            <p className="mt-2 text-sm text-red-600 flex items-center">
              <svg
                className="h-4 w-4 mr-1"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              {cardError}
            </p>
          )}

          {/* Security info */}
          <p className="mt-2 text-sm text-gray-500 flex items-center">
            <svg
              className="h-4 w-4 mr-1 text-green-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                clipRule="evenodd"
              />
            </svg>
            Your payment information is secure and encrypted by Stripe
          </p>
        </div>

        {/* Security & Compliance Info */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-2 flex items-center">
            <svg
              className="h-5 w-5 mr-2 text-green-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            Payment Security
          </h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li className="flex items-center">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
              256-bit SSL encryption
            </li>
            <li className="flex items-center">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
              PCI DSS Level 1 compliant
            </li>
            <li className="flex items-center">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
              No card data stored on our servers
            </li>
            <li className="flex items-center">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
              14-day free trial included
            </li>
          </ul>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={!stripe || processing || disabled || !cardComplete}
          className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {processing ? (
            <div className="flex items-center justify-center">
              <LoadingSpinner size="sm" />
              <span className="ml-2">Processing payment...</span>
            </div>
          ) : (
            <span className="flex items-center justify-center">
              <svg
                className="h-5 w-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              Start Free Trial -{" "}
              {subscription.tier === "standard" ? "Standard" : "Premium"}
            </span>
          )}
        </button>

        {/* Trial info */}
        <p className="text-xs text-gray-500 text-center">
          You won't be charged until your 14-day trial ends. Cancel anytime
          during the trial period.
        </p>

        {/* Support info */}
        <div className="text-center">
          <p className="text-xs text-gray-400">
            Need help? Contact our{" "}
            <a
              href="/support"
              className="text-primary-600 hover:text-primary-700 underline"
            >
              24/7 support team
            </a>
          </p>
        </div>
      </form>
    </div>
  );
};

export default StripeCheckout;
